import React, { memo, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Flex,
  Heading,
  Image,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Th,
  Tr,
  VStack,
} from "@chakra-ui/react"

import { useSanityContent } from "@app/hooks/useSanity"
import { useImage } from "@app/hooks/useImage"
import { FormButton } from "@app/components/Form/FormButton"
import { Icon } from "@app/components/Icon"
import { useGlobalContext } from "@app/providers/global"

type SizingTableRow = {
  key: string
  value: string
}

type SizingTable = {
  key: string
  columns: Array<SizingTableRow>
}

type Props = {
  handleToggle: () => void
  isOpen: boolean
  sizing: GatsbyTypes.SanitySettingSizing
}

export const ProductSizingTable: React.FC<Props> = memo(({ handleToggle, isOpen, sizing }) => {
  const { additionalClose } = useGlobalContext()
  const { product } = useStaticQuery<GatsbyTypes.ProductSizingTableQuery>(graphql`
    query ProductSizingTable {
      product: sanityTemplateProduct {
        additionalSizing
        additionalSizingUnitCentimetre
        additionalSizingUnitInch
        additionalSizingMeasure
        additionalSizingMeasureDescription
        additionalSizingFootnote
      }
    }
  `)
  const { getGatsbyImage } = useImage()
  const { sanityContent } = useSanityContent()

  const instructions = useMemo(() => sanityContent(sizing?.instructions), [sanityContent, sizing?.instructions])

  const image = useMemo(() => getGatsbyImage(sizing?.image, { width: 1000 }), [getGatsbyImage, sizing])

  const rows: Array<SizingTable> = useMemo(
    () =>
      sizing?.table?.rows?.length > 0
        ? sizing.table.rows.map(({ key, cells }) => ({
            key,
            columns: cells?.map((value, index) => ({
              key: `${key}-${index}`,
              value,
            })),
          }))
        : [],
    [sizing]
  )

  return sizing ? (
    <Drawer isOpen={isOpen} placement="right" size="xl" onClose={handleToggle} returnFocusOnClose={false}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader bg="white" p={[5, 8, 12]}>
          <Flex alignItems="center" justifyContent="flex-end">
            <FormButton onClick={handleToggle} title={additionalClose} aria-label={additionalClose} p={2} variant="ghost">
              <Icon name="close" width={5} />
            </FormButton>
          </Flex>
          <Heading as="p" color="grey.lake" fontSize="xl" lineHeight={1.75} mt={[2, 4.75]} mb={3} size="h4" textAlign="center">
            {product?.additionalSizing}
          </Heading>
        </DrawerHeader>
        <DrawerBody bg="white" py={0} px={[7.5, 24.25, 12.5]}>
          <Flex alignItems="stretch" justifyContent="flex-start" flexDirection="column" textAlign="center">
            <Box flex={1}>
              {rows?.length > 0 && (
                <Box mb={[14, 16, 18.75]} mx={[-7.5, 0]} overflowX="auto" overflowY="hidden">
                  <Table minW="full" mx={[5, 0]} variant="rounded">
                    <Thead>
                      <Tr borderBottom="none">
                        {rows[0]?.columns?.map(({ key, value }) => (
                          <Th key={key} whiteSpace="nowrap" dangerouslySetInnerHTML={{ __html: value || "&nbsp;" }} />
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {rows.slice(1)?.map(({ key, columns }) => (
                        <Tr key={key}>
                          {columns?.map(({ key, value }) => (
                            <Td key={key} whiteSpace="nowrap">
                              {value}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
              <VStack alignItems="center" justifyContent="flex-start" spacing={[14, 16, 18.75]}>
                <VStack alignItems="center" justifyContent="flex-start" spacing={2} maxW={325}>
                  <Text letterSpacing="generous" size="xl">
                    {product?.additionalSizingMeasure}
                  </Text>
                  <Text letterSpacing="generous" size="xs">
                    {product?.additionalSizingMeasureDescription}
                  </Text>
                </VStack>
                {image && <Image {...image} maxW={350} />}
                {instructions && (
                  <Text as="div" maxW={325} letterSpacing="generous" size="xs">
                    {instructions}
                  </Text>
                )}
              </VStack>
            </Box>
            <Text
              bg="grey.cloud"
              flexShrink={0}
              py={7.5}
              px={14}
              mt={[14, 12, 16]}
              mx={[-7.5, -24.25, -12.5]}
              letterSpacing="generous"
              size="xs"
            >
              {product?.additionalSizingFootnote}
            </Text>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  ) : null
})
